import { useState, useEffect } from 'react';
import { useOnScopeChange } from '@frontend/scope';

interface PaginationState {
  pageIndex: number;
  rowsPerPage: number;
}
interface PhoneFeature {
  key: string;
  basePath: string;
}

const phoneFeatures: PhoneFeature[] = [
  { key: 'call-groups-pagination', basePath: '/phone/call-groups' },
  { key: 'call-queues-pagination', basePath: '/phone/call-queues' },
  { key: 'device-settings-pagination', basePath: '/phone/devices' },
  { key: 'phone-trees-pagination', basePath: '/phone/phone-tree' },
  { key: 'voicemail-boxes-pagination', basePath: '/phone/voicemail-box' },
];

export const paginationStore: Record<string, PaginationState> = {};

export function usePersistentPagination(storageKey: string) {
  const defaultRowsPerPage = 10;

  const [pagination, setPagination] = useState<PaginationState>(() => {
    return (
      paginationStore[storageKey] ?? {
        pageIndex: 0,
        rowsPerPage: defaultRowsPerPage,
      }
    );
  });

  const paginationTableProps = {
    pageIndex: pagination.pageIndex,
    setPageIndex: (newPage: number) => setPagination((prev) => ({ ...prev, pageIndex: newPage })),
    rowsPerPage: pagination.rowsPerPage,
    setRowsPerPage: (newRows: number) =>
      setPagination((prev) => {
        const newPageIndex = Math.floor((prev.pageIndex * prev.rowsPerPage) / newRows);
        return {
          ...prev,
          rowsPerPage: newRows,
          pageIndex: newPageIndex,
        };
      }),
  };

  const paginationConfig = {
    clientPaginationConfig: {
      initialPage: pagination.pageIndex,
      defaultRowsPerPage: pagination.rowsPerPage,
      rowsPerPageOptions: [10, 20, 30],
      onPaginationChange: (newPage: number) => setPagination((prev) => ({ ...prev, pageIndex: newPage })),
      onRowsPerPageChange: (newRows: number) =>
        setPagination((prev) => {
          const newPageIndex = Math.floor((prev.pageIndex * prev.rowsPerPage) / newRows);
          return {
            ...prev,
            rowsPerPage: newRows,
            pageIndex: newPageIndex,
          };
        }),
    },
  };

  useEffect(() => {
    paginationStore[storageKey] = pagination;
  }, [pagination, storageKey]);

  useOnScopeChange(() => {
    delete paginationStore[storageKey];
  });

  return [pagination, setPagination, paginationTableProps, paginationConfig] as const;
}

export function cleanupPhonePagination(pathname: string) {
  for (const { key, basePath } of phoneFeatures) {
    if (paginationStore[key] && !pathname.startsWith(basePath)) {
      delete paginationStore[key];
    }
  }
}
